/**
 * Phase 105-Agent-A — Map-first agent task page.
 *
 * Route: /dashboard/agent/tasks (RoleGate: agent only)
 *
 * Layout:
 *   • Top bar — title + task-count badge + Refresh + Online/Offline toggle
 *   • Main canvas — full-bleed Leaflet map with one pin per task
 *   • Right pane (desktop) / bottom-sheet (mobile) — TaskDetailSheet on
 *     pin tap
 *   • Mobile sticky bottom action bar is rendered INSIDE TaskDetailSheet
 *     (its footer is always visible)
 *
 * Phase 105-Agent-B polish:
 *   • `?filter=assigned|pending|delivered` query-param consumer that
 *     filters pins + counts. Driven from the agent Dashboard's three
 *     clickable snapshot cards.
 *   • Filter chip in the top bar with an X to clear.
 *
 * Polls /api/agent/my-tasks every 15 seconds while online; pauses when
 * offline.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw, Package, AlertTriangle, MapPin, X } from "lucide-react";
import RoleGate from "../../components/RoleGate";
import TodaysSnapshot from "../../components/TodaysSnapshot";
import api from "../../services/api";
import OnlineToggle from "../../components/agent/OnlineToggle";
import TaskPinLayer from "../../components/agent/TaskPinLayerWithClustering";
import TaskDetailSheet from "../../components/agent/TaskDetailSheet";
import RejectReasonModal from "../../components/agent/RejectReasonModal";
import PickupDropPolyline from "../../components/agent/PickupDropPolyline";
import AgentPin from "../../components/agent/AgentPin";
import NearestTaskBanner from "../../components/agent/NearestTaskBanner";
import useAgentLocation from "../../hooks/useAgentLocation";
import {
  MapContainer,
  TileLayer,
  useMap,
  DEFAULT_TILE_URL,
  DEFAULT_TILE_ATTRIBUTION,
  L,
} from "../../components/tracking/mapProvider";

const POLL_MS = 15_000;

// Default center if no tasks and no last location — Bangalore (agent.blr's hub).
const DEFAULT_CENTER = [12.9716, 77.5946];

// Phase-B polish — bucket definitions for the ?filter= query param.
// Buckets correspond 1:1 with the Dashboard snapshot cards.
const FILTER_BUCKETS = {
  assigned: {
    label: "Assigned to me",
    statuses: ["assigned", "pickup_assigned", "accepted", "picked_up", "in_transit",
               "linehaul", "at_origin_hub", "at_dest_hub", "out_for_delivery"],
  },
  pending: {
    label: "Pending pickup",
    statuses: ["pending", "assigned", "pickup_assigned", "accepted"],
  },
  delivered: {
    label: "Delivered",
    statuses: ["delivered"],
  },
};

function FitToTasks({ tasks }) {
  const map = useMap();
  const key = useMemo(() => (tasks || []).map((t) => t.tracking_id).join("|"), [tasks]);
  useEffect(() => {
    const points = (tasks || []).map((t) => {
      const lat = t?.pickup_location?.lat ?? t?.pickup_lat;
      const lng = t?.pickup_location?.lng ?? t?.pickup_lng;
      return (lat != null && lng != null) ? [Number(lat), Number(lng)] : null;
    }).filter(Boolean);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [50, 50] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

function Inner() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [selectedTid, setSelectedTid] = useState(null);
  const [rejectTask, setRejectTask] = useState(null);
  const timerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKey = searchParams.get("filter");
  const activeBucket = filterKey && FILTER_BUCKETS[filterKey] ? FILTER_BUCKETS[filterKey] : null;

  // Phase 105-Agent-C — live location share, paused while offline.
  const agentLoc = useAgentLocation({ enabled: !offline });

  const load = async () => {
    try {
      const { data } = await api.get("/agent/my-tasks");
      setTasks(data?.rows || []);
      setOffline(!!data?.offline);
      setError(null);
    } catch (e) {
      const code = e?.response?.status;
      setError(code === 403 ? "Only verified agents can access this page." : "Couldn't load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // When the agent toggles online/offline we want an immediate refresh
  // so the pins react instantly (not after the 15-s tick).
  const handleStatusChange = () => { load(); };

  // Apply ?filter= bucket if present. Empty bucket → render the empty
  // state under the chip so the agent knows the filter is filtering
  // (not that they have no tasks).
  const visibleTasks = useMemo(() => {
    if (!activeBucket) return tasks;
    const allow = new Set(activeBucket.statuses);
    return (tasks || []).filter((t) => allow.has(String(t.status || "").toLowerCase()));
  }, [tasks, activeBucket]);

  const selected = useMemo(
    () => (visibleTasks || []).find((t) => t.tracking_id === selectedTid) || null,
    [visibleTasks, selectedTid],
  );

  const removeTaskLocally = (tid) => {
    setTasks((curr) => curr.filter((t) => t.tracking_id !== tid));
    if (selectedTid === tid) setSelectedTid(null);
  };

  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };

  const totalForChip = visibleTasks.length;
  const grandTotal = tasks.length;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="agent-tasks-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="agent-tasks-topbar">
        <div className="min-w-0">
          <h1 className="font-['Outfit'] text-lg sm:text-xl font-bold text-slate-900">My tasks</h1>
          <p className="text-xs text-slate-500">
            {offline
              ? "You're offline — pickups paused."
              : activeBucket
                ? `${totalForChip} of ${grandTotal} task${grandTotal === 1 ? "" : "s"} match the filter`
                : `${grandTotal} active task${grandTotal === 1 ? "" : "s"} on the map`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {activeBucket && (
            <button
              type="button"
              onClick={clearFilter}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-orange-50 hover:bg-orange-100 border border-orange-200 text-xs font-semibold text-[#FF6B00] transition"
              data-testid="agent-tasks-filter-chip"
              title={`Filter: ${activeBucket.label} · click to clear`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
              {activeBucket.label}
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 transition"
            data-testid="agent-tasks-refresh-btn"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </button>
          <OnlineToggle onChange={handleStatusChange} />
        </div>
      </div>

      <TodaysSnapshot />

      {/* Phase 105-Agent-C — nearest pickup suggestion (hidden when no
          tasks or no location). */}
      <NearestTaskBanner
        hasLocation={!!agentLoc.lat}
        onSelect={(tid) => {
          setSelectedTid(tid);
          // Clear any active filter so the targeted pin is actually visible.
          if (activeBucket) clearFilter();
        }}
      />

      {/* Map + side sheet */}
      <div className="flex-1 relative flex" data-testid="agent-tasks-canvas">
        <div className="flex-1 relative">
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6" data-testid="agent-tasks-error">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <p className="mt-3 text-sm font-semibold text-slate-700">{error}</p>
              <button onClick={load} className="mt-3 px-4 py-2 rounded-xl bg-[#FF6B00] text-white text-xs font-bold hover:bg-orange-600">Try again</button>
            </div>
          ) : offline ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6" data-testid="agent-tasks-offline">
              <MapPin className="h-10 w-10 text-slate-400" />
              <p className="mt-3 text-sm font-semibold text-slate-700">You're offline.</p>
              <p className="text-xs text-slate-500 mt-1 max-w-xs">Toggle "Online" in the top bar to start receiving pickups.</p>
            </div>
          ) : (visibleTasks.length === 0 && !loading) ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6" data-testid="agent-tasks-empty">
              <Package className="h-10 w-10 text-slate-400" />
              <p className="mt-3 text-sm font-semibold text-slate-700">
                {activeBucket ? `No tasks matching "${activeBucket.label}".` : "No tasks assigned yet."}
              </p>
              <p className="text-xs text-slate-500 mt-1 max-w-xs">
                {activeBucket
                  ? "Try clearing the filter to see all your assignments."
                  : "When dispatch hands you a pickup it'll appear here as a pin on the map."}
              </p>
              {activeBucket && (
                <button
                  type="button"
                  onClick={clearFilter}
                  className="mt-3 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700"
                  data-testid="agent-tasks-empty-clear-filter"
                >
                  Clear filter
                </button>
              )}
            </div>
          ) : (
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={11}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%", minHeight: 380 }}
              data-testid="agent-tasks-map"
            >
              <TileLayer url={DEFAULT_TILE_URL} attribution={DEFAULT_TILE_ATTRIBUTION} />
              <FitToTasks tasks={visibleTasks} />
              <TaskPinLayer
                tasks={visibleTasks}
                onSelect={(t) => setSelectedTid(t.tracking_id)}
                selectedTrackingId={selectedTid}
              />
              <AgentPin
                lat={agentLoc.lat}
                lng={agentLoc.lng}
                accuracyM={agentLoc.accuracy_m}
                paused={agentLoc.paused}
                lastSentAt={agentLoc.lastSentAt}
              />
              {selected && <PickupDropPolyline task={selected} />}
            </MapContainer>
          )}

          {/* Legend */}
          {!error && !offline && visibleTasks.length > 0 && (
            <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur rounded-xl border border-slate-200 shadow px-3 py-2 text-[11px] text-slate-700 space-y-1" data-testid="agent-tasks-legend">
              <p className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Pin legend</p>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> New / pending</div>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Pending acceptance</div>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Accepted</div>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Picked / in transit</div>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#FF6B00]" /> Out for delivery</div>
            </div>
          )}
        </div>

        {/* Side sheet */}
        {selected && (
          <TaskDetailSheet
            task={selected}
            onClose={() => setSelectedTid(null)}
            onAccept={(tid) => {
              // Refetch so the pin color flips green and the accept CTA
              // disables on the now-accepted row.
              load();
              setSelectedTid(tid);
            }}
            onRejectClick={(t) => setRejectTask(t)}
            onRefresh={load}
            onTaskUpdated={load}
          />
        )}
      </div>

      <RejectReasonModal
        open={!!rejectTask}
        onOpenChange={(v) => { if (!v) setRejectTask(null); }}
        trackingId={rejectTask?.tracking_id}
        onRejected={(tid) => { removeTaskLocally(tid); load(); }}
      />
    </div>
  );
}

export default function AgentTasks() {
  return (
    <RoleGate allow={["agent"]} fallbackTo="/dashboard">
      <Inner />
    </RoleGate>
  );
}
